import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import Checkbox from '@repo/ui/checkbox'
import { DateRangeInput } from '@repo/ui/date-range-input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import RadioInput from '@repo/ui/radio-input'
import SelectInput from '@repo/ui/select-input'
import Switch from '@repo/ui/switch'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getCenters } from '../../../api/centers/get-centers'
import { getCountries } from '../../../api/countries/get-countries'
import { getFormulas } from '../../../api/formula/get-formulas'
import { getPeriods } from '../../../api/periods/get-periods'
import { getRads } from '../../../api/rads/get-rads'
import { getTrips } from '../../../api/trips/get-trips'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  AccomodationEnum,
  accomodationEnumMapping
} from '../../../lib/types/enums/accomodation-type/accomodation-type'
import { AffectedStaffStatusEnum } from '../../../lib/types/enums/affected-staff/affected-staff-status.enum'
import {
  StaffPositionEnum,
  staffPositionEnumMapping
} from '../../../lib/types/enums/staff/staff-position.enum'
import {
  StaffQualificationEnum,
  staffQualificationEnumMapping
} from '../../../lib/types/enums/staff/staff-qualifications.enum'
import { AffectedStaffFilters } from '../../../lib/types/models/affectedStaff/affected-staff-input'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import CountryFlag from '../../elements/country-flag'

interface Props {
  filter?: AffectedStaffFilters
  onFilterChange: (filter: AffectedStaffFilters) => void
}

export function AffectedStaffsFilters({ filter, onFilterChange }: Props) {
  function setFilter<K extends keyof AffectedStaffFilters>(
    key: K,
    value: AffectedStaffFilters[K]
  ) {
    onFilterChange({ ...filter, [key]: value })
  }

  const { t } = useTranslate()

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  const { data: rads } = useQuery({
    queryKey: ['rads'],
    queryFn: () => getRads()
  })

  const { data: trips } = useQuery({
    queryKey: ['trips'],
    queryFn: () => getTrips({ version: 'BASIC' })
  })

  const { data: centers } = useQuery({
    queryKey: ['centers', 'BASIC'],
    queryFn: () => getCenters({ version: 'BASIC' })
  })

  return (
    <div className={'flex flex-col gap-3'}>
      <div />

      <div className="flex flex-col gap-1">
        <Typography variant="label-medium" color={tailwindColors.grey.DEFAULT}>
          Document administratif
        </Typography>
        <div className="flex items-center gap-5">
          <Checkbox
            label="Dossier complet"
            checked={filter?.allDocumentsCompleted}
            onChange={(checked) =>
              onFilterChange({
                ...filter,
                allDocumentsCompleted: checked,
                hasMissingDocuments: checked ? undefined : filter?.hasMissingDocuments
              })
            }
          />
          <Checkbox
            label="Dossier incomplet"
            checked={filter?.hasMissingDocuments}
            onChange={(checked) =>
              onFilterChange({
                ...filter,
                hasMissingDocuments: checked,
                allDocumentsCompleted: checked ? undefined : filter?.allDocumentsCompleted
              })
            }
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Typography variant="label-medium" color={tailwindColors.grey.DEFAULT}>
          Type d&apos;encadrant
        </Typography>
        <div className="flex items-center gap-5">
          <RadioInput
            label="Tous"
            checked={filter?.isFrench === undefined}
            onCheck={() => setFilter('isFrench', undefined)}
          />
          <RadioInput
            label="Voir les encadrants UK"
            checked={filter?.isFrench === false}
            onCheck={() => setFilter('isFrench', false)}
          />
          <RadioInput
            label="Voir les encadrants FR"
            checked={filter?.isFrench === true}
            onCheck={() => setFilter('isFrench', true)}
          />
        </div>
      </div>

      <MultiSelectInput
        label={'Pays'}
        variant="primary"
        searchable
        options={countries?.data.map(({ name, code }) => ({
          label: name,
          value: code,
          custom: <CountryFlag possibleCountry={name} />
        }))}
        selectedOptions={filter?.countries?.map((code) => ({
          label: countries?.data.find((c) => c.code === code)?.name ?? '',
          value: code
        }))}
        onChange={(countryIds) => setFilter('countries', countryIds)}
      />

      <MultiSelectInput
        label={'Périodes'}
        variant={'warning'}
        searchable
        options={periods?.data?.map((period) => ({ label: period.name, value: period.id }))}
        onChange={(periods) => setFilter('periods', periods)}
        selectedOptions={filter?.periods?.map((periodId) => ({
          label: periods?.data?.find((p) => p.id === periodId)?.name || '',
          value: periodId
        }))}
      />

      <MultiSelectInput
        label={'Formule de séjour'}
        variant={'primary'}
        searchable
        options={formulas?.data?.map((formula) => ({
          label: getTranslatedName(formula),
          value: formula.id
        }))}
        onChange={(formulas) => setFilter('formulas', formulas)}
        selectedOptions={filter?.formulas?.map((formulaId) => ({
          label: getTranslatedName(formulas?.data?.find((f) => f.id === formulaId)),
          value: formulaId
        }))}
      />

      <MultiSelectInput
        label={'Qualifications'}
        variant={'primary'}
        searchable
        options={Object.values(StaffQualificationEnum).map((qualification) => ({
          label: t(staffQualificationEnumMapping[qualification].label),
          value: qualification
        }))}
        onChange={(qualifications) => setFilter('qualifications', qualifications)}
        selectedOptions={filter?.qualifications?.map((qualification) => ({
          label: t(staffQualificationEnumMapping[qualification].label),
          value: qualification
        }))}
      />

      <MultiSelectInput
        label={'Poste'}
        variant={'primary'}
        searchable
        options={Object.values(StaffPositionEnum)
          .filter((position) => position !== StaffPositionEnum.AAT)
          .map((position) => ({
            label: t(staffPositionEnumMapping[position]),
            value: position
          }))}
        onChange={(positions) => setFilter('positions', positions)}
        selectedOptions={filter?.positions?.map((position) => ({
          label: t(staffPositionEnumMapping[position]),
          value: position
        }))}
      />

      <DateRangeInput
        label={'Date du départ'}
        value={
          filter?.departureDateRange?.startDate && filter?.departureDateRange?.endDate
            ? {
                from: DateFormatter.toDate(filter?.departureDateRange.startDate),
                to: DateFormatter.toDate(filter?.departureDateRange.endDate)
              }
            : undefined
        }
        onClearDate={() => setFilter('departureDateRange', undefined)}
        onDateRangeChange={(dateRange) => {
          if (dateRange?.from && dateRange?.to)
            setFilter('departureDateRange', {
              startDate: DateFormatter.toDateFormatted(dateRange.from),
              endDate: DateFormatter.toDateFormatted(dateRange.to)
            })
        }}
      />

      <DateRangeInput
        label={'Date du retour'}
        value={
          filter?.returnDateRange?.startDate && filter?.returnDateRange?.endDate
            ? {
                from: DateFormatter.toDate(filter?.returnDateRange.startDate),
                to: DateFormatter.toDate(filter?.returnDateRange.endDate)
              }
            : undefined
        }
        onClearDate={() => setFilter('returnDateRange', undefined)}
        onDateRangeChange={(dateRange) => {
          if (dateRange?.from && dateRange?.to)
            setFilter('returnDateRange', {
              startDate: DateFormatter.toDateFormatted(dateRange.from),
              endDate: DateFormatter.toDateFormatted(dateRange.to)
            })
        }}
      />

      <DateRangeInput
        label={"Date d'affectation"}
        value={
          filter?.assignedDateRange?.startDate && filter?.assignedDateRange?.endDate
            ? {
                from: DateFormatter.toDate(filter?.assignedDateRange.startDate),
                to: DateFormatter.toDate(filter?.assignedDateRange.endDate)
              }
            : undefined
        }
        onClearDate={() => setFilter('assignedDateRange', undefined)}
        onDateRangeChange={(dateRange) => {
          if (dateRange?.from && dateRange?.to)
            setFilter('assignedDateRange', {
              startDate: DateFormatter.toDateFormatted(dateRange.from),
              endDate: DateFormatter.toDateFormatted(dateRange.to)
            })
        }}
      />

      <SelectInput
        label="RAD"
        options={
          rads?.data.map((rad) => ({
            label: rad.title,
            value: rad.id
          })) ?? []
        }
        onChange={(options) => setFilter('radId', options)}
        selectedOption={
          filter?.radId
            ? {
                label: rads?.data.find((c) => c.id === filter.radId)?.title ?? '',
                value: filter.radId
              }
            : undefined
        }
      />

      <MultiSelectInput
        label={'Centre'}
        variant={'primary'}
        searchable
        options={centers?.data.map((center) => ({ label: center.name, value: center.id }))}
        selectedOptions={filter?.centerIds?.map((centerId) => ({
          label: centers?.data.find((center) => center.id === centerId)?.name ?? '',
          value: centerId
        }))}
        onChange={(centerIds) => setFilter('centerIds', centerIds)}
      />

      <MultiSelectInput
        label={"Type d'hébergement"}
        variant={'primary'}
        searchable
        options={Object.values(AccomodationEnum).map((option) => ({
          label: accomodationEnumMapping[option],
          value: option
        }))}
        selectedOptions={filter?.accomodations?.map((accommodation) => ({
          label: accomodationEnumMapping[accommodation],
          value: accommodation
        }))}
        onChange={(accomodations) => setFilter('accomodations', accomodations)}
      />

      <MultiSelectInput
        label={'Voyage'}
        variant={'primary'}
        searchable
        options={trips?.data?.map((trip) => ({
          label: TripProperties.getTripLabel(trip),
          value: trip.id
        }))}
        selectedOptions={filter?.trips?.map((tripId) => ({
          label: TripProperties.getTripLabel(trips?.data?.find((t) => t.id === tripId)),
          value: tripId
        }))}
        onChange={(trips) => setFilter('trips', trips)}
      />

      <Switch
        label="À confirmer"
        labelVariant="label-medium"
        checked={
          filter?.statuses?.length === 1 &&
          filter?.statuses?.includes(AffectedStaffStatusEnum.PENDING)
        }
        onCheckedChange={(check) => {
          if (check) {
            setFilter('statuses', [AffectedStaffStatusEnum.PENDING])
          } else {
            setFilter('statuses', [
              AffectedStaffStatusEnum.AFFECTED,
              AffectedStaffStatusEnum.PENDING
            ])
          }
        }}
      />
    </div>
  )
}
